import clsx from 'clsx';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useCarList from '../../hooks/react-query/useCarData';
import useOrders from '../../hooks/react-query/useOrder';
import Carousel from 'react-multi-carousel';
import { SliderCar } from '../../components/cars/sliderCar';
import { RightArrow, LeftArrow } from '../../icons/arrows/carouselArrows';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDriversByPlateNumberFn } from '../../transactions/drivers/drivers';
import {
  useDeleteDrivers,
  useSetPrimaryDriver,
  useUnAssignDrivers,
} from '../../hooks/react-query/useDrivers';
import { useServiceCarStore } from '../../store/serviceCarStore';
import { OffersListing } from '../../components/cars/offersListing';
import { filterOrderedCarList } from '../../utils/helper';
import usePersonStore from '../../store/personStore';
import { PAGES, } from '../../config/constants';
import BannerAlert from '../../components/ui/BannerAlert';
import { useGetDeliveryStatus } from '../../hooks/react-query/useVehicleDelivery';
import { useLoyaltyProducts } from '../../hooks/react-query/useLoyalty';

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 100 },
    items: 1,
    // partialVisibilityGutter: 500
  },
};

export const RetailDashboard = () => {
  const {
    LOYALTY,
  } = PAGES;
  
  const pageAccess = usePersonStore((store) => store.pageAccess);
  const { isLoyaltyEligible, isLoyaltyEnrolled } = usePersonStore.getState();

  const assignedRoles = usePersonStore((store) => store.userRoles);
  const hasOnlyDriversRole =
    assignedRoles.length === 1 && assignedRoles.includes('driver');

  const [type, setType] = useState();
  const [activeCar, setActiveCar] = useState(false);
  const [orderCarList, setOrderCarList] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [isPrepared, setIsPrepared] = useState(false);
  const processedKeyRef = useRef("");

  const { setCarInfo } = useServiceCarStore();

  const queryClient = useQueryClient();
  const { data: carList, dataUpdatedAt: carUpdatedAt, isLoading: isCarListLoading } = useCarList();
  const { data: ordersList, isLoading: isOrdersListLoading } = useOrders(!hasOnlyDriversRole);
  useLoyaltyProducts({enabled: !!isLoyaltyEligible && !!isLoyaltyEnrolled})
  

  const { mutate: deleteDriver } = useDeleteDrivers();
  const { mutate: unAssignDriver } = useUnAssignDrivers();
  const { mutate: setPrimaryDriver } = useSetPrimaryDriver();

  const plateno = useMemo(()=> {return activeCar?.plateno || activeCar?.vahPlate || null},[activeCar])
  
  const {
    isLoading: driversListLoading,
    data: driversList,
  } = useQuery({
    queryKey: ['usersPerCar', plateno],
    queryFn: () => getDriversByPlateNumberFn(plateno),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: !!activeCar && !!plateno && !hasOnlyDriversRole,
  });

  const { data: deliveryInfo, isLoading: isDeliveryInfoLoading} = useGetDeliveryStatus({
    plateno: plateno, 
    enabled:!!activeCar && !!plateno && (activeCar?.isDeliveryEligible === 1 || ["1006", "1007",].includes(activeCar?.orderLine?.code))
  })
  
  useLayoutEffect(() => {
    if (hasOnlyDriversRole) {
      if (!carList?.data?.length) {
        setIsPrepared(false);
        return;
      }
      const key = `drivers-${carUpdatedAt}`;
      if (processedKeyRef.current === key) {
        setIsPrepared(true);
        return;
      }
      processedKeyRef.current = key;
      setType('leased');
      setActiveCar((prev) => prev || carList.data[0]);
      setCarInfo(carList.data[0]);
      const orderList = filterOrderedCarList({}, carList);
      setOrderCarList(orderList);
      setIsPrepared(true);
      return;

    } else {
      if (isCarListLoading || isOrdersListLoading) {
        setIsPrepared(false);
        return;
      }
      if (!carList || !ordersList) {
        setIsPrepared(false);
        return;
      }

      // Build a cheap “version key” so we don’t re-run mutation unnecessarily
      const key = `${carList?.data?.length ?? 0}-${ordersList?.data?.length ?? 0}`;
      if (processedKeyRef.current === key) {
        setIsPrepared(true);
        return;
      }

      processedKeyRef.current = key;

      // IMPORTANT: this does a desired mutation behavior:
      // filterOrderedCarList mutates `carList` and produces `orderList`
      const orderList = filterOrderedCarList(ordersList, carList);
      setOrderCarList(orderList);
      
      if (carList && carList.data?.length) {
        setType('leased');
        setActiveCar(carList.data[0]);
        setCarInfo(carList.data[0]);
      } else if (orderCarList && orderCarList.data && orderCarList.data.length) {
        setType('ordered');
      }
      
      setIsPrepared(true);
    }
    
    
  }, [carList, ordersList, isCarListLoading, isOrdersListLoading, activeCar, type, setCarInfo, hasOnlyDriversRole]);


  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Add event listener for resize
    window.addEventListener('resize', handleResize);

    // Clean up by removing the event listener
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const bannerRef = useRef();
  
  useEffect(() => {
    if(((activeCar?.orderLine && ["1006"].includes(activeCar?.orderLine?.code) && (!!activeCar?.vehPlate || !!activeCar?.plateno)) || activeCar?.quantity>2) && activeCar?.hasAssignedDriver === false) {
      bannerRef.current?.show("Ολοκλήρωσε άμεσα τη διαδικασία δήλωσης κύριου οδηγού για να προγραμματιστεί η παραλαβή του αυτοκινήτου.", "bg-[#F97316]");
    } else  bannerRef.current?.hide();
  }, [activeCar, driversList, ordersList]);

  const handleClickAction = (actionType, id) => {
    switch (actionType) {
      case 'setPrimaryDriver':
        setPrimaryDriver(
          { userId: id, plateno: plateno },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ['usersPerCar'],
              });
            },
          }
        );
        break;
      case 'unAssignDriver':
        unAssignDriver(
          { id, plateno: plateno },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ['usersPerCar'],
              });
              // queryClient.invalidateQueries({
              //   queryKey: [CARS],
              // });
            },
          }
        );
        break;
      case 'deleteDriver':
        deleteDriver(id, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['usersPerCar'] });
            // queryClient.invalidateQueries({
            //   queryKey: [CARS],
            // });
          },
        });
        break;
      default:
        break;
    }
  };

  const CustomButtonGroup = ({ next, previous, ...rest }) => {
    const { carouselState } = rest;
    return (
      <>
        {activeCar && (
          <div className="w-full items-center justify-center max-lg:gap-6 lg:gap-14 flex pt-5">
            {carList.data.length !== 1 &&
              <div
              className={clsx("h-10 w-10 rounded-full bg-white flex items-center justify-center", carouselState.currentSlide === 0 ? 'opacity-70' : 'cursor-pointer')}
              onClick={() => carouselState.currentSlide !== 0 && previous()}
            >
              <LeftArrow />
            </div>
            }
            <h2 className='text-center'>
              {activeCar.carTitle ?? activeCar.ModelName + ' ' +activeCar.MakeName}
            </h2>
            {carList.data.length !== 1 &&
              <div
                className={clsx("h-10 w-10 rounded-full bg-white flex items-center justify-center", carouselState.currentSlide === carouselState.totalItems - 1 ? 'opacity-70' : ' cursor-pointer')}
                onClick={() => carouselState.currentSlide !== carouselState.totalItems - 1 && next()}
              >
                <RightArrow />
              </div>
            }
          </div>
        )}
      </>
    );
  };

  return (
    <>
      {!driversListLoading && <BannerAlert ref={bannerRef}/>}
      <div
        className={'overflow-auto scrollbar max-lg:flex max-lg:flex-col'}
        style={{
          height: 'calc(100vh - 140px)',
        }}
        >
        <div className="pt-10 container mx-auto flex items-center justify-center relative">
          <h1 className="max-xl:hidden absolute left-0">My Cars</h1>
          {!hasOnlyDriversRole && (
            <div
            className={clsx(
                'flex rounded-[8px] p-1 h-fit',
                type !== undefined && ' border  border-[#A5A5A5]'
              )}
              >
              {carList && carList.data?.length ? (
                <button
                className={clsx(
                  'text-secondary-button py-2 px-8 cursor-pointer rounded-[6px]',
                  type === 'leased'
                  ? 'text-white bg-black'
                  : 'text-[#6A6A6A] bg-transparent'
                )}
                onClick={() => {
                  setActiveCar(carList.data[0]);
                  setCarInfo(carList.data[0]);
                  setType('leased');
                }}
                >
                  Μισθώσεις
                </button>
              ) : (
                <></>
              )}

              {orderCarList && orderCarList.data?.length ? (
                <button
                className={clsx(
                  'text-secondary-button py-2 px-8 cursor-pointer rounded-[6px]',
                  type === 'ordered'
                  ? 'text-white bg-black'
                  : 'text-[#6A6A6A] bg-transparent'
                )}
                onClick={() => {
                  setType('ordered');
                }}
                >
                Εκκρεμείς Παραγγελίες
                </button>
              ) : (
                <></>
              )}
            </div>
          )}
        </div>

        {type === 'leased' ? (
          <div className="w-full flex flex-col max-lg:flex-1">
            {!isCarListLoading && ((!hasOnlyDriversRole && !isOrdersListLoading) || hasOnlyDriversRole) && isPrepared && <Carousel
              responsive={responsive}
              infinite={false}
              transitionDuration={0}
              containerClass={clsx(
                'w-full order-last max-lg:h-full',
                carList?.data?.length === 1 && 'justify-center',
                carList?.data?.length &&
                carList?.data?.length > 1 &&
                (((plateno !== undefined || plateno !== null) &&
                  plateno === carList?.data[0].plateno) ||
                  (activeCar?.cncode !== undefined &&
                    activeCar?.cncode === carList?.data[0]?.cncode)) &&
                    'lg:pl-[25vw]'
              )}
              //this was commented out for center mode and pl was 25
              // itemClass='flex justify-center !w-fit'
              sliderClass="max-lg:h-full"
              arrows={false}
              centerMode={windowWidth > 990}
              renderButtonGroupOutside
              partialVisible={windowWidth < 990}
              beforeChange={(nextSlide, props) => {
                setCarInfo(carList.data[nextSlide]);
                setActiveCar(carList.data[nextSlide]);
              }}
              swipeable={false}
              draggable={false}
              customButtonGroup={<CustomButtonGroup />}
              >
              {carList?.data?.map((item, index) => {
                return (
                <SliderCar
                handleClickAction={(type, id) => {
                  handleClickAction(type, id);
                  }}
                  deliveryInfo={deliveryInfo ?? undefined}
                  deliveryInfoLoading = {isDeliveryInfoLoading}
                  driversList={driversList?.data}
                  carInfo={item}
                  key={item.plateno || item.cncode}
                  hasOnlyDriversRole={hasOnlyDriversRole}
                  active={
                    (item?.plateno && item?.plateno === plateno) ||
                    (item?.cncode && item?.cncode === activeCar.cncode)
                  }
                  />
                )})}
            </Carousel>}
          </div>
        ) : (
          !isOrdersListLoading && orderCarList && orderCarList?.data &&
            <OffersListing ordersList={orderCarList?.data} />
          )}
      </div>
    </>
  );
};
